import { z } from "zod";

import { VerificationResult } from "@/src/models/task-verification";

/**
 * What ops can do from a step card: one verdict and one way on, the same for every customer. Which a
 * card offers is derived on the server (src/lib/step-cards/actions.ts) and re-checked when one is
 * submitted; the server picks what Continue does.
 */
export enum StepCardActionType {
  /** "Continue": ops let the run go on from this card (see `StepCardContinueVia`). */
  CONTINUE = "continue",
  /** Correct / Incorrect on automation's result. */
  VERDICT = "verdict",
}

/** What Continue does on a card, chosen by the server from what the run waits on. */
export enum StepCardContinueVia {
  /** Releases the pending approval the card holds, through the approval's own checks, and records Correct. */
  APPROVAL = "approval",
  /** Runs the `resumable` step the run is parked on again, which re-checks what it waited for. */
  RESUME = "resume",
}

/**
 * One action submitted from a card. `revision` is the card's revision on the page (when any row behind
 * it last changed), and Continue's `targetId` names the row or approval it acts on, so
 * a click made against a page that has since moved on is refused rather than applied to something else.
 */
export const stepCardActionRequestSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal(StepCardActionType.CONTINUE), targetId: z.uuid(), revision: z.string() }),
  z.object({
    type: z.literal(StepCardActionType.VERDICT),
    verdict: z.enum(VerificationResult),
    revision: z.string(),
  }),
]);

export type StepCardActionRequest = z.infer<typeof stepCardActionRequestSchema>;

export const stepCardActionResponseSchema = z.object({
  success: z.literal(true),
  /** Whether the action restarted the workflow. */
  resumed: z.boolean(),
});

export type StepCardActionResponse = z.infer<typeof stepCardActionResponseSchema>;
